// Monatskalender des DatePickers (DESIGN §3.6.7): Raster 7 × 6, Zellen 36 rund, heute mit Ring
// 1 px `fg-tertiary`, gewählt `accent`/`on-accent`, Bereich als Fläche `accent-soft`.
// ARIA `grid` mit `aria-selected`; Roving-Fokus mit Pfeilen, PageUp/PageDown, Home/End.
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { addMonths, format, isSameMonth, startOfMonth } from 'date-fns';
import { formatCellLabel, fromIso, inRange, monthGrid, moveByKey, toIso } from './_lib/calendar';
import { IconButton } from './IconButton';
import { cn } from '../basis/kern';

export interface CalendarProps {
  /** Gewählter Tag bzw. Bereichsanfang (ISO). */
  selected?: string | null;
  /** Bereichsende (ISO) im Bereichsmodus. */
  rangeEnd?: string | null;
  /** „Heute“ (APP_TODAY), ISO. */
  today: string;
  onSelect: (iso: string) => void;
  /** Startmonat, wenn nichts gewählt ist (ISO-Tag). */
  initialFocus?: string;
  min?: string;
  max?: string;
  /** Fokus beim Öffnen in die Zelle setzen (im Popover). */
  focusOnMount?: boolean;
  className?: string;
}

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const WEEKDAY_NAMES = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

export function Calendar({
  selected,
  rangeEnd,
  today,
  onSelect,
  initialFocus,
  min,
  max,
  focusOnMount,
  className,
}: CalendarProps) {
  const start = fromIso(selected) ?? fromIso(initialFocus) ?? fromIso(today) ?? new Date();
  const [focused, setFocused] = useState<Date>(start);
  const [shouldFocus, setShouldFocus] = useState(Boolean(focusOnMount));
  const gridRef = useRef<HTMLTableElement>(null);
  const month = startOfMonth(focused);
  const days = monthGrid(month);
  const focusedIso = toIso(focused);

  useEffect(() => {
    if (!shouldFocus) return;
    gridRef.current?.querySelector<HTMLButtonElement>(`[data-iso="${focusedIso}"]`)?.focus();
  }, [focusedIso, shouldFocus]);

  const outOfBounds = (iso: string) =>
    (min !== undefined && iso < min) || (max !== undefined && iso > max);

  const onKeyDown = (event: KeyboardEvent<HTMLTableElement>) => {
    const next = moveByKey(focused, event.key, event.shiftKey);
    if (!next) return;
    event.preventDefault();
    setShouldFocus(true);
    setFocused(next);
  };

  const showMonth = (delta: number) => {
    setShouldFocus(false);
    setFocused((d) => addMonths(d, delta));
  };

  const title = format(month, 'MMMM yyyy');
  return (
    <div className={cn('w-[268px]', className)}>
      <div className="mb-2 flex items-center justify-between">
        <span aria-live="polite" className="pl-2 text-button text-fg">
          {title}
        </span>
        <span className="flex gap-1">
          <IconButton
            label="Previous month"
            icon={<CaretLeftIcon />}
            variant="ghost"
            surface="card"
            size="sm"
            tooltip={false}
            onClick={() => showMonth(-1)}
          />
          <IconButton
            label="Next month"
            icon={<CaretRightIcon />}
            variant="ghost"
            surface="card"
            size="sm"
            tooltip={false}
            onClick={() => showMonth(1)}
          />
        </span>
      </div>
      <table
        ref={gridRef}
        role="grid"
        aria-label={title}
        onKeyDown={onKeyDown}
        className="w-full border-collapse"
      >
        <thead>
          <tr>
            {WEEKDAYS.map((day, i) => (
              <th
                key={day}
                scope="col"
                abbr={WEEKDAY_NAMES[i]}
                className="h-8 text-center text-chip text-fg-tertiary"
              >
                {day}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 6 }, (_, row) => (
            <tr key={row}>
              {days.slice(row * 7, row * 7 + 7).map((day) => {
                const iso = toIso(day);
                const isSelected = iso === selected || iso === rangeEnd;
                const between = inRange(iso, selected, rangeEnd) && !isSelected;
                const disabled = outOfBounds(iso);
                return (
                  <td
                    key={iso}
                    role="gridcell"
                    aria-selected={isSelected}
                    className={cn('p-0 text-center', between && 'bg-accent-soft')}
                  >
                    <button
                      type="button"
                      data-iso={iso}
                      tabIndex={iso === focusedIso ? 0 : -1}
                      aria-label={formatCellLabel(day)}
                      aria-current={iso === today ? 'date' : undefined}
                      disabled={disabled}
                      onClick={() => {
                        setFocused(day);
                        onSelect(iso);
                      }}
                      onFocus={() => setFocused(day)}
                      className={cn(
                        'inline-flex size-9 items-center justify-center rounded-full text-label tabular-nums',
                        'transition-colors duration-(--bar-dur-fast) ease-standard focus-visible:outline-offset-0',
                        isSameMonth(day, month) ? 'text-fg' : 'text-fg-tertiary',
                        iso === today && !isSelected && 'ring-1 ring-fg-tertiary ring-inset',
                        isSelected ? 'bg-accent text-on-accent' : 'hover:not-disabled:bg-tile',
                        'disabled:cursor-not-allowed disabled:text-fg-disabled',
                      )}
                    >
                      {day.getDate()}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
